/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Deeply match a route tree given a location
 */

import type { Location } from 'history'
import invariant from 'tiny-invariant'
import { matchPath } from './matchPath'
import type { Branch, ZumperRoute } from './types'

// https://github.com/remix-run/react-router/blob/v5/packages/react-router-config/modules/matchRoutes.js
const matchRoute =
  <Params extends { [K in keyof Params]?: string | undefined } = {}>(
    location: Location<unknown>,
    branch: Branch<Params>
  ) =>
  (route: ZumperRoute<Params>) => {
    const { pathname } = location
    const match = matchPath(pathname, route)
    if (match) {
      branch.push({ match, route })
      const routes = route.routes || route.anyRoutes
      if (routes?.length) {
        const useSwitch = !!route.routes
        matchRoutes(routes, location, { branch, useSwitch })
      }
    }
    return match !== null
  }

export const matchRoutes = <
  Params extends { [K in keyof Params]?: string | undefined },
>(
  routes: ZumperRoute<Params>[],
  location: Location,
  {
    branch = [],
    useSwitch = true,
  }: { branch?: Branch<Params>; useSwitch?: boolean } = {}
): Branch<Params> => {
  invariant(Array.isArray(routes), 'routes must be an array')
  if (useSwitch) {
    routes.some(matchRoute(location, branch))
  } else {
    routes.forEach(matchRoute(location, branch))
  }
  return branch
}
